import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Dashboard from './pages/Dashboard';
import EventSetup from './pages/EventSetup';
import QRCodePage from './pages/QRCodePage';
import PublicGuestSearch from './pages/PublicGuestSearch';
import PublicInvite from './pages/PublicInvite';
import PublicInviteRsvp from './pages/PublicInviteRsvp';
// Phase 1.9.22 — Premium Story showcase. React.lazy so the binding
// chain isn't followed eagerly by the visual-edits babel plugin.
const PremiumStoryShowcase = React.lazy(() => import('./pages/PremiumStoryShowcase'));
import RsvpPageEditor from './pages/RsvpPageEditor';
import Broadcast from './pages/Broadcast';
import Admin from './pages/Admin';
import Subscription from './pages/Subscription';
import PaymentSuccess from './pages/PaymentSuccess';
import InstagramStory from './pages/InstagramStory';
import AuthCallback from './pages/AuthCallback';
import TemplatePreview from './pages/TemplatePreview';
import TemplatePicker from './pages/TemplatePicker';
import TemplateLibraryPage from './pages/TemplateLibraryPage';
import InternalTemplatePreviewPage from './pages/InternalTemplatePreviewPage';
import TemplateEditorPage from './pages/TemplateEditorPage';
import PublicDemoPage from './pages/PublicDemoPage';
import PublicReviewPage from './pages/PublicReviewPage';
import InvitationEditor from './pages/InvitationEditor';
import EditorPreviewPage from './pages/EditorPreviewPage';
import RevealPage from './pages/RevealPage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-pulse text-[#D4AF37] font-heading text-xl">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, isFirstLogin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-pulse text-[#D4AF37] font-heading text-xl">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    // Preserve isFirstLogin state when redirecting to dashboard
    return <Navigate to="/dashboard" state={{ isFirstLogin }} replace />;
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Detect session_id from Google OAuth redirect — must run synchronously before routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public guest search - no auth needed */}
      <Route path="/event/:slug" element={<PublicGuestSearch />} />
      <Route path="/event/:slug/broadcast" element={
        <ProtectedRoute>
          <Broadcast />
        </ProtectedRoute>
      } />
      
      {/* Public RSVP invitation page - no auth needed */}
      <Route path="/invite/:slug" element={<PublicInvite />} />
      <Route path="/invite/:slug/rsvp" element={<PublicInviteRsvp />} />
      {/* Phase 1.9.22 — Premium Story dedicated showcase URL (no auth).
          Renders the classic PremiumStory template directly so client
          demos work independently of the broken classic-editor URL. */}
      <Route path="/story/:eventId" element={
        <React.Suspense fallback={null}>
          <PremiumStoryShowcase />
        </React.Suspense>
      } />
      <Route path="/reveal/:token" element={<RevealPage />} />
      <Route path="/:slug/morning-reveal/:token" element={<RevealPage />} />
      <Route path="/preview/template/:templateId" element={<TemplatePreview />} />
      {/* Phase 2.1 Lite — Public demo URL (anonymous). */}
      <Route path="/demo/:slug" element={<PublicDemoPage />} />
      {/* Phase 2.2 Lite — Token-gated client review URL (anonymous, noindex). */}
      <Route path="/review/:eventSlug" element={<PublicReviewPage />} />
      <Route path="/editor-preview" element={<EditorPreviewPage />} />
      
      {/* Public static pages */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      
      {/* Auth routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId" element={
        <ProtectedRoute>
          <EventSetup />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/qr" element={
        <ProtectedRoute>
          <QRCodePage />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/template-picker" element={
        <ProtectedRoute>
          <TemplatePicker />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/invitation-editor" element={
        <ProtectedRoute>
          <InvitationEditor />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/rsvp-editor" element={
        <ProtectedRoute>
          <RsvpPageEditor />
        </ProtectedRoute>
      } />

      {/* Phase 2.0 Lite — Internal Template Library (admin-only on the
          server side; we still gate the route behind ProtectedRoute). */}
      <Route path="/studio/templates" element={
        <ProtectedRoute>
          <TemplateLibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/templates/:templateId/preview" element={
        <ProtectedRoute>
          <InternalTemplatePreviewPage />
        </ProtectedRoute>
      } />
      {/* Phase 2.0.1 — Edit template in Studio. */}
      <Route path="/studio/templates/:templateId/edit" element={
        <ProtectedRoute>
          <TemplateEditorPage />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/instagram-story" element={<InstagramStory />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Admin />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <Subscription />
        </ProtectedRoute>
      } />
      <Route path="/payment-success" element={
        <ProtectedRoute>
          <PaymentSuccess />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <LanguageProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </LanguageProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
